import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  RocketLaunchIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import * as api from '../../services/api';
import AgentDownload from './AgentDownload';
import ReleaseUploadModal from '../releases/ReleaseUploadModal';

/**
 * Strip markdown formatting from release notes for compact display.
 * Removes headers, list prefixes, collapses whitespace, and truncates.
 */
function formatReleaseNotes(notes: string, maxLength = 200): string {
  if (!notes) return '';
  let text = notes
    // Strip markdown headers
    .replace(/^#{1,6}\s+/gm, '')
    // Strip markdown list prefixes
    .replace(/^[\s]*[-*]\s+/gm, '')
    // Strip bold/italic markers
    .replace(/[*_]{1,3}/g, '')
    // Collapse multiple newlines to single newline
    .replace(/\n{2,}/g, '\n')
    // Collapse multiple spaces
    .replace(/ {2,}/g, ' ')
    .trim();
  if (text.length > maxLength) {
    text = text.substring(0, maxLength).trimEnd() + '...';
  }
  return text;
}

interface Release {
  id: string;
  version: string;
  release_notes: string;
  is_mandatory: boolean;
  is_active: boolean;
  published_at: string;
  rollout_percentage: number;
  download_count: number;
  paused_at: string | null;
  deactivated_at: string | null;
}

const statusStyles: Record<string, string> = {
  ONLINE: 'bg-anchor-teal/10 text-anchor-teal border-anchor-teal/20',
  OFFLINE: 'bg-white/[0.04] text-white/40 border-white/10',
  ERROR: 'bg-red-500/10 text-red-400 border-red-500/20',
  MAINTENANCE: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
};

const formatCompactDate = (iso: string | null): string => {
  if (!iso) return '--';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
};

const AgentSettings: React.FC = () => {
  const navigate = useNavigate();
  const [agents, setAgents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState<string | null>(null);

  // Releases state
  const [releases, setReleases] = useState<Release[]>([]);
  const [releasesLoading, setReleasesLoading] = useState(true);
  const [releasesError, setReleasesError] = useState<string | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);

  const fetchAgents = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getAgents();
      setAgents(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load agents');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAgents(); }, [fetchAgents]);

  const fetchReleases = useCallback(async () => {
    try {
      setReleasesLoading(true);
      const data = await api.getAgentReleases();
      setReleases(Array.isArray(data) ? data : []);
      setReleasesError(null);
    } catch (err) {
      setReleasesError(err instanceof Error ? err.message : 'Failed to load releases');
    } finally {
      setReleasesLoading(false);
    }
  }, []);

  useEffect(() => { fetchReleases(); }, [fetchReleases]);

  const handleSync = async (agentId: string) => {
    setSyncing(agentId);
    try {
      await api.synchronizeAgent(agentId);
    } catch (err) {
      // Sync is best-effort
    } finally {
      setSyncing(null);
    }
  };

  const onlineCount = agents.filter(a => a.status === 'ONLINE').length;
  const offlineCount = agents.filter(a => a.status === 'OFFLINE').length;
  const errorCount = agents.filter(a => a.status === 'ERROR').length;

  return (
    <div className="space-y-6">
      <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-lg font-medium text-white">Registered Agents</h3>
            <p className="text-sm text-white/40 mt-0.5">{agents.length} agents across your organization</p>
          </div>
          <div className="flex gap-2">
            {onlineCount > 0 && <span className="text-xs py-1 px-2 rounded bg-anchor-teal/10 text-anchor-teal">{onlineCount} online</span>}
            {offlineCount > 0 && <span className="text-xs py-1 px-2 rounded bg-white/[0.04] text-white/40">{offlineCount} offline</span>}
            {errorCount > 0 && <span className="text-xs py-1 px-2 rounded bg-red-500/10 text-red-400">{errorCount} error</span>}
            <button onClick={fetchAgents} className="text-xs text-anchor-blue hover:text-white transition-colors px-2 py-1 rounded border border-anchor-blue/30">
              Refresh
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-anchor-blue"></div>
          </div>
        ) : error ? (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-center">
            <p className="text-sm text-red-300">{error}</p>
            <button onClick={fetchAgents} className="mt-2 text-xs text-anchor-blue hover:text-white transition-colors">Retry</button>
          </div>
        ) : agents.length === 0 ? (
          <div className="text-center py-12 text-white/30 text-sm">No agents registered yet. Download the agent installer below.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/[0.06]">
                  <th className="text-left py-2.5 px-3 text-[10px] text-white/30 uppercase tracking-wider font-medium">Hostname</th>
                  <th className="text-left py-2.5 px-3 text-[10px] text-white/30 uppercase tracking-wider font-medium">Status</th>
                  <th className="text-left py-2.5 px-3 text-[10px] text-white/30 uppercase tracking-wider font-medium">Version</th>
                  <th className="text-left py-2.5 px-3 text-[10px] text-white/30 uppercase tracking-wider font-medium">Last Seen</th>
                  <th className="text-right py-2.5 px-3 text-[10px] text-white/30 uppercase tracking-wider font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {agents.map((agent: any) => (
                  <tr key={agent.id} className="border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors">
                    <td className="py-3 px-3 text-white/80 font-mono text-xs">{agent.hostname || agent.name}</td>
                    <td className="py-3 px-3">
                      <span className={`text-[10px] py-0.5 px-2 rounded-full border ${statusStyles[agent.status] || statusStyles.OFFLINE}`}>
                        {agent.status}
                      </span>
                    </td>
                    <td className="py-3 px-3 text-white/50 text-xs">{agent.agentVersion || '—'}</td>
                    <td className="py-3 px-3 text-white/40 text-xs">{agent.lastSeenAt ? new Date(agent.lastSeenAt).toLocaleString() : '—'}</td>
                    <td className="py-3 px-3 text-right">
                      <button
                        onClick={() => handleSync(agent.id)}
                        disabled={syncing === agent.id}
                        className="text-[10px] text-anchor-blue hover:text-white transition-colors px-2 py-0.5 rounded border border-anchor-blue/30 hover:border-anchor-blue disabled:opacity-50"
                      >
                        {syncing === agent.id ? 'Syncing...' : 'Synchronize'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AgentDownload />

      {/* Agent Releases Section */}
      <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <RocketLaunchIcon className="h-5 w-5 text-anchor-blue" />
            <div>
              <h3 className="text-lg font-medium text-white">Agent Releases</h3>
              <p className="text-sm text-white/40 mt-0.5">Manage agent binary releases and rollouts</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => navigate('/releases')}
              className="text-xs text-anchor-blue hover:text-white transition-colors px-2 py-1 rounded border border-anchor-blue/30"
            >
              Manage Releases
            </button>
            <button
              onClick={() => setShowUploadModal(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-white bg-anchor-blue rounded-lg hover:bg-anchor-blue/80 transition-colors"
            >
              <PlusIcon className="h-3.5 w-3.5" />
              Upload New Release
            </button>
          </div>
        </div>

        {releasesLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-anchor-blue"></div>
          </div>
        ) : releasesError ? (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-center">
            <p className="text-sm text-red-300">{releasesError}</p>
            <button onClick={fetchReleases} className="mt-2 text-xs text-anchor-blue hover:text-white transition-colors">Retry</button>
          </div>
        ) : releases.length === 0 ? (
          <div className="text-center py-8 text-white/30 text-sm">
            No releases published yet. Upload your first agent release.
          </div>
        ) : (
          <div className="space-y-1">
            {releases.slice(0, 5).map((r) => (
              <div
                key={r.id}
                className="bg-white/[0.02] border border-white/[0.04] rounded-lg px-4 py-3 hover:bg-white/[0.04] transition-colors"
              >
                <div className="flex items-center gap-3 mb-1">
                  <span className="text-sm font-medium text-white">v{r.version}</span>
                  <span className="text-xs text-white/40">&mdash;</span>
                  <span className="text-xs text-white/40">{formatCompactDate(r.published_at)}</span>
                  <span className="text-xs text-white/40">&middot;</span>
                  {r.is_mandatory ? (
                    <span className="text-[10px] px-1.5 py-0.5 bg-red-500/15 text-red-400 border border-red-500/20 rounded">
                      Mandatory
                    </span>
                  ) : (
                    <span className="text-[10px] px-1.5 py-0.5 bg-white/[0.04] text-white/40 border border-white/[0.08] rounded">
                      Optional
                    </span>
                  )}
                  <span className="text-xs text-white/40">&middot;</span>
                  <span className="text-xs text-anchor-blue">{r.rollout_percentage}% rolled out</span>
                  {r.paused_at && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 rounded">
                      Paused
                    </span>
                  )}
                  {r.deactivated_at && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-red-500/10 text-red-400 border border-red-500/20 rounded">
                      Deactivated
                    </span>
                  )}
                </div>
                {r.release_notes && (
                  <p className="text-xs text-white/50 leading-relaxed line-clamp-2">
                    {formatReleaseNotes(r.release_notes)}
                  </p>
                )}
              </div>
            ))}
            {releases.length > 5 && (
              <div className="pt-2 text-center">
                <button
                  onClick={() => navigate('/releases')}
                  className="text-xs text-anchor-blue hover:text-white transition-colors"
                >
                  View all {releases.length} releases
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Release Upload Modal */}
      {showUploadModal && (
        <ReleaseUploadModal
          onClose={() => setShowUploadModal(false)}
          onUploaded={() => {
            setShowUploadModal(false);
            fetchReleases();
          }}
        />
      )}
    </div>
  );
};

export default AgentSettings;
